import React, { useEffect, useRef, useState } from "react";
import "./ESGSection.css";

export type CarouselImage = {
  src: string;
  alt?: string;
  caption?: string;
};

export type ESGSlide = {
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  images?: CarouselImage[];
  items?: string[];
};

type Props = {
  slides?: ESGSlide[];
  ctaLabel?: string;
  onCtaClick?: () => void;
};

const defaultSlides: ESGSlide[] = [
  {
    title: "Clothing",
    description:
      "Premium apparel, uniforms, and workwear collections tailored to different teams, roles, and environments.",
    images: [
      {
        src: "/imgs/webpic/3/10.png",
        alt: "Professional clothing",
        caption: "Uniforms and apparel for hospitality, retail, and industry.",
      },
      {
        src: "/imgs/webpic/3/11.png",
        alt: "Custom workwear",
        caption: "Consistent brand presentation across your whole team.",
      },
      {
        src: "/imgs/webpic/3/12.png",
        alt: "Custom workwear",
        caption: "Consistent brand presentation across your whole team.",
      },
      {
        src: "/imgs/webpic/3/13.png",
        alt: "Custom workwear",
        caption: "Consistent brand presentation across your whole team.",
      },
      {
        src: "/imgs/webpic/3/14.png",
        alt: "Custom workwear",
        caption: "Consistent brand presentation across your whole team.",
      },
    ],
    items: [
      "Bulk orders with size-range support",
      "Quality fabrics for everyday use",
      "Embroidery and print customization",
    ],
  },  
  {
    title: "Promotional Products",
    description:
      "Branded merchandise and campaign materials designed to strengthen visibility and create memorable touchpoints.",
    images: [
      {
        src: "/imgs/webpic/2/4.png",
        alt: "Promotional products",
        caption: "Corporate gifts, branded accessories, and campaign material.",
      },
      {
        src: "/imgs/webpic/2/5.png",
        alt: "Custom branded products",
        caption: "Flexible production for events, retail, and internal campaigns.",
      },
      {
        src: "/imgs/webpic/2/6.png",
        alt: "Custom branded products",
        caption: "Flexible production for events, retail, and internal campaigns.",
      },
      {
        src: "/imgs/webpic/2/7.png",
        alt: "Custom branded products",
        caption: "Flexible production for events, retail, and internal campaigns.",
      },
      {
        src: "/imgs/webpic/2/8.png",
        alt: "Custom branded products",
        caption: "Flexible production for events, retail, and internal campaigns.",
      },
      {
        src: "/imgs/webpic/2/9.png",
        alt: "Custom branded products",
        caption: "Flexible production for events, retail, and internal campaigns.",
      },
    ],
    items: [
      "Wide product catalog",
      "Quality materials and print finishes",
      "Fast turnaround for campaigns",
      "Fully custom branding options",
    ],
  },
  {
    title: "Personal Protective Equipment",
    description:
      "Safety solutions for demanding workplaces, built around compliance, comfort, and long-term durability.",
    images: [
      {
        src: "/imgs/webpic/1/1.png",
        alt: "Personal Protective Equipment",
        caption: "Certified PPE for industrial and professional use.",
      },
      {
        src: "/imgs/webpic/1/2.png",
        alt: "Protective workwear",
        caption: "High-visibility clothing, gloves, helmets, and more.",
      },
      {
        src: "/imgs/webpic/1/3.png",
        alt: "Protective workwear",
        caption: "High-visibility clothing, gloves, helmets, and more.",
      },
    ],
    items: [
      "Industry standards and compliance support",
      "Durability-tested materials",
      "Bulk ordering for teams and organizations",
      "Custom branding on selected equipment",
    ],
  },

  
];

export default function ESGSection({ slides = defaultSlides }: Props) {
  const [visibleSlides, setVisibleSlides] = useState<Set<number>>(new Set());
  const [activeImageIndexes, setActiveImageIndexes] = useState<number[]>(
    slides.map(() => 0)
  );
  const observerRef = useRef<IntersectionObserver | null>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    setActiveImageIndexes(slides.map(() => 0));
  }, [slides]);

  useEffect(() => {
    const intervalId = window.setInterval(() => {
      setActiveImageIndexes((prev) =>
        slides.map((slide, idx) => {
          const images = getImagesForSlide(slide);
          const currentIndex = prev[idx] ?? 0;

          if (images.length <= 1) {
            return 0;
          }

          return (currentIndex + 1) % images.length;
        })
      );
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [slides]);

  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const idx = Number(entry.target.getAttribute("data-slide-idx"));
            if (!Number.isNaN(idx)) {
              setVisibleSlides((prev) => new Set(prev).add(idx));
            }
          }
        });
      },
      { threshold: 0.2 }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  useEffect(() => {
    const currentSlides = slideRefs.current;

    currentSlides.forEach((el) => {
      if (el && observerRef.current) {
        observerRef.current.observe(el);
      }
    });

    return () => {
      currentSlides.forEach((el) => {
        if (el && observerRef.current) {
          observerRef.current.unobserve(el);
        }
      });
    };
  }, [slides]);

  const getImagesForSlide = (slide: ESGSlide): CarouselImage[] => {
    if (slide.images && slide.images.length > 0) {
      return slide.images;
    }

    return [
      {
        src: slide.image || "/imgs/why.png",
        alt: slide.imageAlt || slide.title,
        caption: slide.imageAlt || slide.title,
      },
    ];
  };

  const changeSlideImage = (slideIndex: number, direction: number, totalImages: number) => {
    setActiveImageIndexes((prev) =>
      prev.map((value, idx) => {
        if (idx !== slideIndex) return value;
        return (value + direction + totalImages) % totalImages;
      })
    );
  };

  return (
    <section className="esg-section">
      <div className="esg-container">
        {slides.map((slide, idx) => {
          const ids = ["why", "how", "who"];
          const anchorId = ids[idx] || undefined;
          const isVisible = visibleSlides.has(idx);
          const images = getImagesForSlide(slide);
          const activeImageIndex = activeImageIndexes[idx] ?? 0;
          const activeImage = images[activeImageIndex];
          const hasCarousel = images.length > 1;

          return (
            <div
              ref={(el) => {
                slideRefs.current[idx] = el;
              }}
              data-slide-idx={idx}
              className={`esg-media-layout${idx % 2 === 1 ? " reverse" : ""}`}
              key={slide.title}
              id={anchorId}
            >
              <div className="esg-content">
                <h2 className={`esg-title${isVisible ? " esg-animate-title" : ""}`}>
                  {slide.title}
                </h2>
                <p className={`esg-description${isVisible ? " esg-animate-desc" : ""}`}>
                  {slide.description}
                </p>

                {slide.items && slide.items.length > 0 && (
                  <ul className={`esg-list${isVisible ? " esg-animate-desc" : ""}`}>
                    {slide.items.map((item) => (
                      <li key={item} className="esg-list-item">
                        {item}
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="esg-media">
                <figure className={`esg-image-card${isVisible ? " esg-animate-img" : ""}`}>
                  <div className="esg-image-wrapper">
                    <img
                      src={activeImage.src}
                      alt={activeImage.alt || slide.title}
                      className="esg-media-img"
                      loading="lazy"
                    />
                    <div className="esg-image-overlay"></div>

                    {hasCarousel && (
                      <>
                        <button
                          type="button"
                          className="esg-carousel-btn prev"
                          aria-label={`Previous image for ${slide.title}`}
                          onClick={() => changeSlideImage(idx, -1, images.length)}
                        >
                          &#8249;
                        </button>
                        <button
                          type="button"
                          className="esg-carousel-btn next"
                          aria-label={`Next image for ${slide.title}`}
                          onClick={() => changeSlideImage(idx, 1, images.length)}
                        >
                          &#8250;
                        </button>
                      </>
                    )}
                  </div>

                  {/* <figcaption className="esg-image-caption">
                    {activeImage.caption || activeImage.alt || slide.title}
                  </figcaption> */}

                  {/* {hasCarousel && (
                    <div className="esg-carousel-dots" aria-label={`${slide.title} image selector`}>
                      {images.map((image, imageIdx) => (
                        <button
                          key={`${image.src}-${imageIdx}`}
                          type="button"
                          className={`esg-carousel-dot${
                            imageIdx === activeImageIndex ? " active" : ""
                          }`}
                          aria-label={`Show image ${imageIdx + 1} for ${slide.title}`}
                          onClick={() => setSlideImageIndex(idx, imageIdx)}
                        />
                      ))}
                    </div>
                  )} */}
                </figure>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
